import React from 'react';
import { Link } from 'react-router-dom';
import { BaseComponentImpl } from '../../../types/implementations';
import { Building, PhotoModel } from '../../../types/protocols';
import { WorkEnvironmentRiskData } from '../../../Helpers/utils';
import ComponentDetailsView from '../../ComponentPage/ComponentDetailsView';
import PhotoGallery from '../../../Components/PhotoGallery';
import { FileText } from 'lucide-react';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { FileAttachment } from '../../../types/attachments';
import '../../Room/RoomComponentDetailsPane.css';

type DetailContent =
    | { type: 'component'; component: BaseComponentImpl }
    | { type: 'room'; risk: WorkEnvironmentRiskData }
    | null;

interface RisksDetailPaneProps {
    content: DetailContent;
    building: Building;
    buildingKey: string;
}

export type { DetailContent };

const RisksDetailPane: React.FC<RisksDetailPaneProps> = ({
    content,
    building,
    buildingKey,
}) => {
    if (content?.type === 'component') {
        return (
            <ComponentDetailsView
                component={content.component}
                building={building}
                buildingKey={buildingKey}
                showHeader={true}
                headerActions={
                    <Link
                        className="open-tab-button"
                        to={`/building/${buildingKey}/component/${content.component.key}`}
                        target="_blank"
                        title="Öppna i ny flik"
                    >
                        ↗
                    </Link>
                }
                containerStyle={{
                    position: 'sticky',
                    top: '20px',
                    maxHeight: 'calc(100vh - 40px)',
                    overflowY: 'auto',
                }}
            />
        );
    }

    if (content?.type === 'room') {
        return <RoomRiskDetail risk={content.risk} buildingKey={buildingKey} />;
    }

    return (
        <div
            style={{
                width: '100%',
                minHeight: '400px',
                border: '2px dashed #ccc',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fafafa',
                position: 'sticky',
                top: '20px',
                maxHeight: 'calc(100vh - 40px)',
                overflowY: 'auto',
            }}
        >
            <p style={{ color: '#666', fontSize: '16px', textAlign: 'center', margin: 0, padding: '20px' }}>
                Klicka på ett objekt för att visa mer info här
            </p>
        </div>
    );
};

// ── Room Risk Detail Sub-component ──────────────────────────────

const RoomRiskDetail: React.FC<{
    risk: WorkEnvironmentRiskData;
    buildingKey: string;
}> = ({ risk, buildingKey }) => {
    const handleOpenAttachment = async (attachment: FileAttachment) => {
        try {
            const storage = getStorage();
            const fileRef = storageRef(storage, attachment.storagePath);
            const url = await getDownloadURL(fileRef);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Failed to open attachment:', err);
        }
    };

    const hasPhotos = risk.photos && Object.keys(risk.photos).length > 0;
    const hasAttachments = risk.attachments && Object.keys(risk.attachments).length > 0;

    return (
        <div
            className="std-box dropshadow"
            style={{
                padding: '20px',
                position: 'sticky',
                top: '20px',
                maxHeight: 'calc(100vh - 40px)',
                overflowY: 'auto',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h4 style={{ margin: 0 }}>{risk.roomLabel}</h4>
                <Link
                    className="open-tab-button"
                    to={`/building/${buildingKey}/room/${risk.roomKey}`}
                    target="_blank"
                    title="Öppna i ny flik"
                >
                    ↗
                </Link>
            </div>

            <div className="hazard-text-color-yellow" style={{
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid',
            }}>
                <h6 style={{ marginBottom: '8px', fontWeight: 600 }}>Arbetsmiljörisk</h6>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '14px' }}>{risk.description}</p>
            </div>

            {hasPhotos && (
                <div style={{ marginBottom: '16px' }}>
                    <h6 style={{ marginBottom: '8px' }}>Foton</h6>
                    <PhotoGallery
                        photos={risk.photos!}
                        buildingKey={buildingKey}
                        thumbnailSize={80}
                    />
                </div>
            )}

            {hasAttachments && (
                <div>
                    <h6 style={{ marginBottom: '8px' }}>Filer</h6>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {Object.entries(risk.attachments!).map(([key, attachment]) => (
                            <li
                                key={key}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '6px 0',
                                    borderBottom: '1px solid #eee',
                                }}
                            >
                                <span
                                    style={{ cursor: 'pointer', color: '#0d6efd', display: 'flex', alignItems: 'center', gap: '6px' }}
                                    onClick={() => handleOpenAttachment(attachment)}
                                    title="Öppna fil"
                                >
                                    <FileText size={14} />
                                    {attachment.fileName}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default RisksDetailPane;
